import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';

import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { appointmentValidation, realtimeValidation } from '../../../utils/validationUtils';


const AppointmentBookingModal = ({ isOpen, onClose, onBook, selectedDate }) => {
  const [formData, setFormData] = useState({
    propertyId: '',
    propertyAddress: '',
    clientName: '',
    clientPhone: '',
    staffId: '',
    staffName: '',
    date: '',
    time: '',
    duration: '60',
    type: 'viewing',
    notes: ''
  });

  const [conflicts, setConflicts] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (selectedDate) {
      setFormData(prev => ({
        ...prev,
        date: format(selectedDate, 'yyyy-MM-dd')
      }));
    }
  }, [selectedDate]);

  const properties = [
    { value: 'AMM-2026-001', label: 'شارع الملكة رانيا، برج الأعمال - عبدون' },
    { value: 'AMM-2026-045', label: 'حي الصويفية، فيلا رقم 234 - الصويفية' },
    { value: 'AMM-2026-089', label: 'شارع الملك حسين، مركز الأعمال - الشميساني' },
    { value: 'AMM-2026-023', label: 'شارع الجامعة، محل تجاري - جبل الحسين' },
    { value: 'AMM-2026-078', label: 'طريق المطار، فيلا فاخرة - الرابية' }
  ];

  const staff = [
    { value: 'staff001', label: 'محمد السعيد' },
    { value: 'staff002', label: 'خالد الغامدي' },
    { value: 'staff003', label: 'أحمد السعيد' },
    { value: 'staff004', label: 'عبدالله الشمري' }
  ];

  const timeSlots = [
    { value: '09:00', label: '09:00 صباحاً' },
    { value: '10:00', label: '10:00 صباحاً' },
    { value: '11:00', label: '11:00 صباحاً' },
    { value: '12:00', label: '12:00 ظهراً' },
    { value: '13:00', label: '01:00 مساءً' },
    { value: '14:00', label: '02:00 مساءً' },
    { value: '15:00', label: '03:00 مساءً' },
    { value: '16:00', label: '04:00 مساءً' },
    { value: '17:00', label: '05:00 مساءً' },
    { value: '18:00', label: '06:00 مساءً' }
  ];

  const durations = [
    { value: '30', label: '30 دقيقة' },
    { value: '45', label: '45 دقيقة' },
    { value: '60', label: '60 دقيقة' },
    { value: '90', label: '90 دقيقة' },
    { value: '120', label: '120 دقيقة' }
  ];

  const handlePropertyChange = (value) => {
    const property = properties?.find(p => p?.value === value);
    setFormData({
      ...formData,
      propertyId: value,
      propertyAddress: property?.label || ''
    });
  };

  const handleStaffChange = (value) => {
    const staffMember = staff?.find(s => s?.value === value);
    setFormData({
      ...formData,
      staffId: value,
      staffName: staffMember?.label || ''
    });

    // Check for conflicts (simplified - in real app would check against actual appointments)
    if (formData?.date && formData?.time) {
      const hasConflict = Math.random() > 0.7; // Simulate conflict detection
      if (hasConflict) {
        setConflicts(['الموظف لديه موعد آخر في هذا الوقت']);
      } else {
        setConflicts([]);
      }
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (validationErrors?.[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
    
    // Real-time validation
    const error = realtimeValidation?.validateField(field, value, { ...formData, [field]: value });
    if (error) {
      setValidationErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleBlur = (field) => {
    const value = formData?.[field];
    const isRequired = ['title', 'appointmentDate']?.includes(field);
    const error = realtimeValidation?.validateOnBlur(field, value, isRequired);
    if (error) {
      setValidationErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleSubmit = async () => {
    // Comprehensive validation
    const validation = appointmentValidation?.validate(formData);
    
    if (!validation?.isValid) {
      setValidationErrors(validation?.errors);
      return;
    }

    if (conflicts?.length > 0) {
      if (!window.confirm('يوجد تعارض في المواعيد. هل تريد المتابعة؟')) {
        return;
      }
    }

    onBook(formData);
    setFormData({
      propertyId: '',
      propertyAddress: '',
      clientName: '',
      clientPhone: '',
      staffId: '',
      staffName: '',
      date: '',
      time: '',
      duration: '60',
      type: 'viewing',
      notes: ''
    });
    setConflicts([]);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="حجز موعد جديد"
      icon="Calendar"
      iconColor="primary"
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            إلغاء
          </Button>
          <Button variant="default" iconName="Check" onClick={handleSubmit}>
            حجز الموعد
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Property Selection */}
        <div>
          <Select
            label="العقار"
            options={properties}
            value={formData?.propertyId}
            onChange={handlePropertyChange}
            required
          />
        </div>

        {/* Client Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="اسم العميل"
            value={formData?.clientName}
            onChange={(e) => setFormData({ ...formData, clientName: e?.target?.value })}
            placeholder="أدخل اسم العميل"
            required
          />
          <Input
            label="رقم الهاتف"
            type="tel"
            value={formData?.clientPhone}
            onChange={(e) => setFormData({ ...formData, clientPhone: e?.target?.value })}
            placeholder="+962XXXXXXXXX"
            required
          />
        </div>

        {/* Date and Time */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="التاريخ"
            type="date"
            value={formData?.date}
            onChange={(e) => setFormData({ ...formData, date: e?.target?.value })}
            required
          />
          <Select
            label="الوقت"
            options={timeSlots}
            value={formData?.time}
            onChange={(value) => setFormData({ ...formData, time: value })}
            required
          />
        </div>

        {/* Staff and Duration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="الموظف المسؤول"
            options={staff}
            value={formData?.staffId}
            onChange={handleStaffChange}
            required
          />
          <Select
            label="المدة"
            options={durations}
            value={formData?.duration}
            onChange={(value) => setFormData({ ...formData, duration: value })}
          />
        </div>

        {/* Conflict Warning */}
        {conflicts?.length > 0 && (
          <div className="flex items-start gap-3 p-4 bg-warning/10 rounded-lg border border-warning/30">
            <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-warning mb-1">تحذير: تعارض في المواعيد</p>
              {conflicts?.map((conflict, idx) => (
                <p key={idx} className="text-xs text-muted-foreground">{conflict}</p>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            ملاحظات
          </label>
          <textarea
            value={formData?.notes}
            onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
            placeholder="أضف ملاحظات حول الموعد..."
            rows={3}
            className="w-full px-4 py-3 text-sm bg-background border-2 border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary resize-none"
          />
        </div>

        {/* Auto Confirmation Info */}
        <div className="flex items-start gap-3 p-4 bg-success/10 rounded-lg border border-success/30">
          <Icon name="MessageSquare" size={20} className="text-success mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-success mb-1">تأكيد تلقائي عبر واتساب</p>
            <p className="text-xs text-muted-foreground">
              سيتم إرسال رسالة تأكيد تلقائية للعميل عبر واتساب تحتوي على تفاصيل الموعد
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AppointmentBookingModal;